import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { useToast } from '../context/ToastContext';
import { getInitials } from '../utils/format';
import CourseAutocomplete from '../components/CourseAutocomplete';
import Icon from '../components/Icon';
import './EditProfile.css';

/* Resize an image File to a max dimension, returning a base64 JPEG string */
function resizeImage(file, maxSize = 400, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(maxSize / img.width, maxSize / img.height, 1);
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function EditProfile() {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  // Photo state
  const [photoPreview, setPhotoPreview] = useState(user?.photo_url || null);
  const [photoData, setPhotoData] = useState(null); // base64 if changed

  // Course state
  const [courses, setCourses] = useState([]);
  const [courseForm, setCourseForm] = useState({ course_name: '', city: '', state: '', is_public: true });
  const [courseFormOpen, setCourseFormOpen] = useState(false);
  const [courseLoading, setCourseLoading] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState(null); // set when course_name came from a typeahead pick, not free text

  // Location detect state
  const [locationStatus, setLocationStatus] = useState(
    user?.latitude != null ? 'Location set' : ''
  );
  const [locationCoords, setLocationCoords] = useState({
    latitude: user?.latitude ?? null,
    longitude: user?.longitude ?? null,
  });

  useEffect(() => {
    api.getMyCourses().then(setCourses).catch(() => {});
  }, []);

  /* ── Photo upload ── */
  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('Please select an image file.'); return; }
    if (file.size > 10 * 1024 * 1024) { setError('Image must be smaller than 10 MB.'); return; }
    try {
      const resized = await resizeImage(file);
      setPhotoPreview(resized);
      setPhotoData(resized);
      setError('');
    } catch {
      setError('Failed to process image. Please try another file.');
    }
  };

  /* ── Location detect ── */
  const handleDetectLocation = () => {
    if (!navigator.geolocation) {
      setLocationStatus('Location isn’t supported in this browser');
      return;
    }
    setLocationStatus('Detecting…');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocationCoords({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
        setLocationStatus('Location detected');
      },
      () => setLocationStatus('Couldn’t detect your location')
    );
  };

  /* ── Course handlers ── */
  const handleAddCourse = async () => {
    if (!courseForm.course_name.trim()) return;
    setCourseLoading(true);
    try {
      const c = await api.addMyCourse(courseForm);
      // If this course wasn't picked from an existing suggestion, it was
      // typed/added via "Add it" — enrich the course directory for everyone.
      // Fire-and-forget: a failure here shouldn't block adding to My Courses.
      if (!selectedCourseId) {
        api.addCourse({
          name: courseForm.course_name,
          city: courseForm.city || undefined,
          state: courseForm.state || undefined,
        }).catch(() => {});
      }
      setCourses(prev => [c, ...prev]);
      setCourseForm({ course_name: '', city: '', state: '', is_public: true });
      setCourseFormOpen(false);
      setSelectedCourseId(null);
    } catch (err) { toast.error(err.message); }
    finally { setCourseLoading(false); }
  };

  const handleToggleCoursePrivacy = async (course) => {
    try {
      const updated = await api.updateMyCourse(course.id, { is_public: !course.is_public });
      setCourses(prev => prev.map(c => c.id === course.id ? updated : c));
    } catch (err) { toast.error(err.message); }
  };

  const handleRemoveCourse = async (courseId) => {
    try {
      await api.deleteMyCourse(courseId);
      setCourses(prev => prev.filter(c => c.id !== courseId));
    } catch (err) { toast.error(err.message); }
  };

  /* ── Form data ── */
  const [formData, setFormData] = useState({
    name: user?.name || '',
    handicap: user?.handicap ?? '',
    home_course: user?.home_course || '',
    city: user?.city || '',
    state: user?.state || '',
    bio: user?.bio || '',
    playing_style: user?.playing_style || 'casual',
    preferred_tees: user?.preferred_tees || 'middle',
    pace_preference: user?.pace_preference || 'moderate',
    ghin_number: user?.ghin_number || '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const updates = {
        name: formData.name,
        handicap: formData.handicap !== '' ? parseFloat(formData.handicap) : null,
        home_course: formData.home_course || null,
        city: formData.city || null,
        state: formData.state || null,
        bio: formData.bio || null,
        playing_style: formData.playing_style,
        preferred_tees: formData.preferred_tees,
        pace_preference: formData.pace_preference,
        ghin_number: formData.ghin_number || null,
        latitude: locationCoords.latitude,
        longitude: locationCoords.longitude,
      };
      if (photoData) updates.photo_url = photoData;
      const updatedUser = await api.updateProfile(updates);
      updateUser(updatedUser);
      navigate('/profile');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="edit-profile-page container">
      <div className="edit-profile-card card">
        <h1>Edit Profile</h1>

        {error && <div className="form-error-banner">{error}</div>}

        <form onSubmit={handleSubmit}>

          {/* ── Profile Photo ── */}
          <div className="form-section photo-section">
            <h2>Profile Photo</h2>
            <div className="photo-upload-row">
              <button
                type="button"
                className="photo-upload-btn"
                onClick={() => fileInputRef.current?.click()}
                title="Click to change photo"
              >
                <div className="avatar avatar-xl photo-avatar">
                  {photoPreview
                    ? <img src={photoPreview} alt="Profile" />
                    : <span>{getInitials(formData.name)}</span>
                  }
                </div>
                <div className="photo-upload-overlay">
                  <Icon name="camera" size={22} />
                </div>
              </button>
              <div className="photo-upload-info">
                <p className="photo-upload-hint">Click your photo to upload a new one</p>
                <p className="photo-upload-sub">JPG, PNG or GIF · Images are resized to 400×400</p>
                {photoData && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => { setPhotoPreview(user?.photo_url || null); setPhotoData(null); }}
                  >
                    Remove new photo
                  </button>
                )}
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handlePhotoChange}
            />
          </div>

          {/* ── Basic Information ── */}
          <div className="form-section">
            <h2>Basic Information</h2>

            <div className="form-group">
              <label className="form-label">Full Name</label>
              <input
                type="text"
                name="name"
                className="form-input"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">City</label>
                <input
                  type="text"
                  name="city"
                  className="form-input"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="San Francisco"
                />
              </div>
              <div className="form-group" style={{ flex: '0 0 90px' }}>
                <label className="form-label">State</label>
                <input
                  type="text"
                  name="state"
                  className="form-input"
                  value={formData.state}
                  onChange={handleChange}
                  placeholder="CA"
                  maxLength={2}
                />
              </div>
            </div>

            <div className="location-detect-row">
              <button type="button" className="location-detect-link" onClick={handleDetectLocation}>
                <Icon name="pin" size={15} /> Auto-detect my location for nearby search
              </button>
              {locationStatus && <span className="location-status">{locationStatus}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">Bio</label>
              <textarea
                name="bio"
                className="form-input form-textarea"
                value={formData.bio}
                onChange={handleChange}
                placeholder="Tell other golfers about yourself..."
                rows={3}
              />
            </div>
          </div>

          {/* ── Golf Details ── */}
          <div className="form-section">
            <h2>Golf Details</h2>

            <div className="form-group">
              <label className="form-label">Home Course</label>
              <CourseAutocomplete
                value={formData.home_course}
                onChange={v => setFormData(prev => ({ ...prev, home_course: v }))}
                onSelect={course => setFormData(prev => ({ ...prev, home_course: course.name }))}
                inlineAdd
                placeholder="Pebble Beach Golf Links"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Handicap</label>
                <input
                  type="number"
                  name="handicap"
                  className="form-input"
                  value={formData.handicap}
                  onChange={handleChange}
                  placeholder="12.5"
                  step="0.1"
                  min="-10"
                  max="54"
                  aria-describedby="handicap-hint"
                />
                <p id="handicap-hint" className="form-hint">Plus handicap? Enter it as a negative — +2.1 is -2.1.</p>
              </div>
              <div className="form-group">
                <label className="form-label">Playing Style</label>
                <select name="playing_style" className="form-input form-select" value={formData.playing_style} onChange={handleChange}>
                  <option value="casual">Casual</option>
                  <option value="competitive">Competitive</option>
                  <option value="social">Social</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">GHIN Number</label>
              <input
                type="text"
                name="ghin_number"
                className="form-input"
                value={formData.ghin_number}
                onChange={handleChange}
                placeholder="1234567"
                maxLength={7}
                inputMode="numeric"
                pattern="\d{7}"
              />
              <p className="form-help">
                Your 7-digit GHIN number — required to join ranked rounds.
                {user?.ghin_verified
                  ? <span className="ghin-status ghin-verified"> Verified</span>
                  : user?.ghin_number
                    ? <span className="ghin-status ghin-pending"> Pending verification</span>
                    : null
                }
              </p>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Preferred Tees</label>
                <select name="preferred_tees" className="form-input form-select" value={formData.preferred_tees} onChange={handleChange}>
                  <option value="forward">Forward</option>
                  <option value="middle">Middle</option>
                  <option value="back">Back</option>
                  <option value="tips">Tips</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Pace Preference</label>
                <select name="pace_preference" className="form-input form-select" value={formData.pace_preference} onChange={handleChange}>
                  <option value="relaxed">Relaxed (4.5+ hrs)</option>
                  <option value="moderate">Moderate (4-4.5 hrs)</option>
                  <option value="quick">Quick (&lt;4 hrs)</option>
                </select>
              </div>
            </div>
          </div>

          {/* ── My Courses ── */}
          <div className="form-section">
            <h2>My Courses</h2>
            <p className="form-help">
              Associate yourself with courses you play. Public associations appear in Course Search so other golfers can find you.
            </p>

            {courses.length > 0 && (
              <div className="my-courses-list">
                {courses.map(c => (
                  <div key={c.id} className="my-course-row">
                    <div className="my-course-info">
                      <span className="my-course-name">{c.course_name}</span>
                      {(c.city || c.state) && (
                        <span className="my-course-loc">{[c.city, c.state].filter(Boolean).join(', ')}</span>
                      )}
                    </div>
                    <div className="my-course-actions">
                      <button
                        type="button"
                        className={`privacy-toggle ${c.is_public ? 'public' : 'private'}`}
                        onClick={() => handleToggleCoursePrivacy(c)}
                        title={c.is_public ? 'Visible in Course Search — click to make private' : 'Private — click to make public'}
                      >
                        <Icon name={c.is_public ? 'globe' : 'lock'} size={14} /> {c.is_public ? 'Public' : 'Private'}
                      </button>
                      <button type="button" className="btn-remove-course" onClick={() => handleRemoveCourse(c.id)} title="Remove" aria-label={`Remove ${c.course_name}`}><Icon name="x" size={16} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {courseFormOpen ? (
              <div className="add-course-form">
                <div className="form-row">
                  <div className="form-group" style={{ flex: 2 }}>
                    <label className="form-label">Course Name *</label>
                    <CourseAutocomplete
                      value={courseForm.course_name}
                      onChange={v => { setCourseForm(p => ({ ...p, course_name: v })); setSelectedCourseId(null); }}
                      onSelect={course => {
                        setCourseForm(p => ({ ...p, course_name: course.name, city: course.city || '', state: course.state || '' }));
                        setSelectedCourseId(course.id);
                      }}
                      placeholder="Pebble Beach Golf Links"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">City</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Pebble Beach"
                      value={courseForm.city}
                      onChange={e => setCourseForm(p => ({ ...p, city: e.target.value }))}
                    />
                  </div>
                  <div className="form-group" style={{ flex: '0 0 72px' }}>
                    <label className="form-label">State</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="CA"
                      maxLength={2}
                      value={courseForm.state}
                      onChange={e => setCourseForm(p => ({ ...p, state: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="add-course-bottom">
                  <label className="privacy-label">
                    <input
                      type="checkbox"
                      checked={courseForm.is_public}
                      onChange={e => setCourseForm(p => ({ ...p, is_public: e.target.checked }))}
                    />
                    <span>Show in Course Search (public)</span>
                  </label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => setCourseFormOpen(false)}>Cancel</button>
                    <button type="button" className="btn btn-primary btn-sm" onClick={handleAddCourse} disabled={courseLoading || !courseForm.course_name.trim()}>
                      {courseLoading ? 'Adding...' : 'Add Course'}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setCourseFormOpen(true)}>
                + Add a Course
              </button>
            )}
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => navigate('/profile')}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditProfile;
